import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, CheckCircle, WarningCircle } from "@phosphor-icons/react";

function LinkRow({ link, onSaved }) {
  const [asin, setAsin] = useState(link.asin);
  const [saving, setSaving] = useState(false);
  const shortUrl = `${window.location.origin}/go/${link.slug}`;
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/products/${link.id}/link`, { asin, external_url: link.external_url });
      toast.success(asin ? "Direct product link saved" : "Switched back to search link");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSaving(false);
  };
  const copy = () => navigator.clipboard?.writeText(shortUrl).then(() => toast.success("Short link copied"));
  return (
    <tr data-testid={`link-row-${link.id}`} className="border-b border-[#E5E0D8] last:border-0 align-middle">
      <td className="p-3">
        <div className="flex items-center gap-3 min-w-[14rem]">
          <img src={link.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
          <div className="min-w-0"><p className="text-sm font-medium text-[#2C2B29] truncate max-w-[16rem]">{link.title}</p><p className="text-xs text-[#5C5A56]">{link.category}</p></div>
        </div>
      </td>
      <td className="p-3">
        {link.link_type === "direct"
          ? <span data-testid={`link-status-${link.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#5B7B53]"><CheckCircle size={14} weight="fill" /> Direct</span>
          : <span data-testid={`link-status-${link.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#B5613F]"><WarningCircle size={14} weight="fill" /> Search</span>}
      </td>
      <td className="p-3">
        <div className="flex gap-2">
          <input data-testid={`link-asin-${link.id}`} value={asin} onChange={(e) => setAsin(e.target.value.toUpperCase())} placeholder="ASIN e.g. B08CFJBZRK" maxLength={10} className="w-36 px-2 py-1.5 rounded border border-[#E5E0D8] text-xs font-mono" />
          <button data-testid={`link-save-${link.id}`} disabled={saving || asin === link.asin} onClick={save} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-[#2C2B29] text-white disabled:opacity-30">Save</button>
        </div>
      </td>
      <td className="p-3">
        <button data-testid={`link-copy-${link.id}`} onClick={copy} className="inline-flex items-center gap-1 text-xs text-[#2C2B29] hover:text-[#5B7B53] font-mono"><Copy size={13} /> /go/{link.slug.slice(0, 22)}</button>
      </td>
      <td className="p-3 text-sm font-semibold text-[#D17A58] text-right">{link.clicks_30d}</td>
      <td className="p-3"><a href={link.affiliate_url} target="_blank" rel="noreferrer" data-testid={`link-test-${link.id}`} className="text-xs underline text-[#5C5A56]">Test</a></td>
    </tr>
  );
}

export function AdminLinks() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const load = () => api.get("/links").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => (data?.links || [])
    .filter((l) => filter === "all" || l.link_type === filter)
    .filter((l) => l.title.toLowerCase().includes(q.toLowerCase())), [data, filter, q]);
  if (!data) return <p className="text-[#5C5A56]">Loading…</p>;
  const direct = data.links.filter((l) => l.link_type === "direct").length;
  return (
    <div data-testid="admin-links">
      <h1 className="font-display text-4xl text-[#2C2B29]">Link Management</h1>
      <p className="text-sm text-[#5C5A56] mt-2 max-w-3xl">Every Buy button uses your tag <b className="font-mono">{data.tag}</b>. "Search" links open Amazon.in search results — paste the product's ASIN (the 10 characters after <span className="font-mono">/dp/</span> in the Amazon URL) to send buyers straight to the product page. Share short links like <span className="font-mono">homehearth.in/go/…</span> on WhatsApp or social — every click is counted.</p>
      <div className="grid sm:grid-cols-3 gap-4 my-6">
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-4"><p className="text-xs text-[#5C5A56]">Total links</p><p data-testid="links-total" className="text-2xl font-semibold">{data.links.length}</p></div>
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-4"><p className="text-xs text-[#5C5A56]">Direct product links</p><p data-testid="links-direct" className="text-2xl font-semibold text-[#5B7B53]">{direct}</p></div>
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-4"><p className="text-xs text-[#5C5A56]">Need an ASIN</p><p data-testid="links-search" className="text-2xl font-semibold text-[#B5613F]">{data.links.length - direct}</p></div>
      </div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input data-testid="links-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm w-64" />
        {[["all", "All"], ["search", "Needs ASIN"], ["direct", "Direct"]].map(([k, label]) => (
          <button key={k} data-testid={`links-filter-${k}`} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-sm border ${filter === k ? "bg-[#2C2B29] text-white border-[#2C2B29]" : "border-[#E5E0D8] bg-white"}`}>{label}</button>
        ))}
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-[#F9F8F6] text-xs uppercase tracking-wider text-[#5C5A56]">
            <tr><th className="p-3">Product</th><th className="p-3">Link</th><th className="p-3">ASIN</th><th className="p-3">Short link</th><th className="p-3 text-right">Clicks (30d)</th><th className="p-3" /></tr>
          </thead>
          <tbody>{rows.map((l) => <LinkRow key={`${l.id}-${l.asin}`} link={l} onSaved={load} />)}</tbody>
        </table>
      </div>
    </div>
  );
}
